import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, RefreshCw } from "lucide-react";
import { load } from "@cashfreepayments/cashfree-js";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { apiRequest } from "../services/api";

export default function GiftCardsPage() {
  const { accessToken, isAuthenticated, isLoading } = useCustomerAuth();
  const [params] = useSearchParams();
  const purchaseId = params.get("purchaseId");
  const [form, setForm] = useState({
    recipientEmail: "",
    recipientPhone: "",
    amount: "1000",
  });
  const [error, setError] = useState("");
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);
  const requestKey = useRef(null);
  const submitting = useRef(false);
  const check = useCallback(async () => {
    if (!purchaseId || !accessToken) return;
    setBusy(true);
    setError("");
    try {
      setPayment(
        await apiRequest("/gift-cards/" + encodeURIComponent(purchaseId), {
          accessToken,
        }),
      );
    } catch (e) {
      setError(e.message || "Unable to verify payment. Please retry.");
    } finally {
      setBusy(false);
    }
  }, [purchaseId, accessToken]);
  useEffect(() => {
    void check();
  }, [check]);
  const change = (event) => {
    requestKey.current = null;
    setForm((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  };
  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      requestKey.current ||= crypto.randomUUID();
      const session = await apiRequest("/gift-cards", {
        method: "POST",
        accessToken,
        body: {
          ...form,
          amount: Number(form.amount),
          requestKey: requestKey.current,
        },
      });
      // Retain the purchase reference before leaving the website.
      window.history.replaceState(
        null,
        "",
        "/gift-cards?purchaseId=" + encodeURIComponent(session.id),
      );
      const sdk = await load({
        mode:
          process.env.REACT_APP_CASHFREE_ENV === "production"
            ? "production"
            : "sandbox",
      });
      const result = await sdk.checkout({
        paymentSessionId: session.paymentSessionId,
        redirectTarget: "_self",
      });
      if (result?.error)
        throw new Error(result.error.message || "Unable to open payment.");
    } catch (e) {
      setError(e.message || "Unable to start gift purchase.");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  const money = (value) => "₹" + Number(value).toLocaleString("en-IN");
  return (
    <main className="mx-auto max-w-5xl px-4 py-12 sm:py-20">
      <div className="grid gap-10 md:grid-cols-2">
        <section className="rounded-3xl bg-primary-800 p-8 text-white">
          <Gift size={40} />
          <p className="mt-6 text-xs uppercase tracking-widest">
            CHARMS gift cards
          </p>
          <h1 className="mt-3 text-4xl font-semibold">
            A gift they can choose.
          </h1>
          <p className="mt-5 leading-7">
            Gift loyalty points to someone who already has a CHARMS account.
            Every ₹1 adds 1 loyalty point.
          </p>
          <p className="mt-5 leading-7">
            Use their registered email and phone number. Points are added only
            after verified payment, and their history shows your name as the
            sender.
          </p>
          <p className="mt-5 text-sm">
            They can use points on the CHARMS website or at the store through
            their existing customer account.
          </p>
        </section>
        <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <h2 className="text-2xl font-semibold">
            {purchaseId ? "Gift payment status" : "Buy a gift card"}
          </h2>
          {error && (
            <p
              role="alert"
              className="mt-4 rounded-xl bg-red-50 p-4 text-red-700"
            >
              {error}
            </p>
          )}
          {isLoading ? (
            <p className="mt-6">Loading account…</p>
          ) : !isAuthenticated ? (
            <div className="mt-6">
              <p>
                Sign in to your CHARMS customer account to buy or check a gift.
              </p>
              <Link
                to="/signin"
                state={{
                  from:
                    "/gift-cards" +
                    (purchaseId
                      ? "?purchaseId=" + encodeURIComponent(purchaseId)
                      : ""),
                }}
                className="mt-4 inline-block rounded-xl bg-primary-800 px-6 py-3 text-white"
              >
                Sign in
              </Link>
            </div>
          ) : purchaseId ? (
            <div className="mt-6 space-y-5">
              {payment && (
                <>
                  <p className="text-3xl font-semibold">
                    {money(payment.amount)} ·{" "}
                    {payment.amount.toLocaleString("en-IN")} points
                  </p>
                  <p role="status">
                    {payment.status === "CREDITED"
                      ? "Gift delivered! The recipient’s points have been credited."
                      : payment.status === "PAID_PENDING_CREDIT"
                        ? "Payment received. Please contact CHARMS support with this reference so we can complete delivery."
                        : payment.status === "FAILED"
                          ? "This payment was not completed. You can start a new gift."
                          : "Payment is awaiting confirmation. Refresh to check again."}
                  </p>
                </>
              )}
              <p className="break-all text-xs text-slate-500">
                Reference: {purchaseId}
              </p>
              <button
                type="button"
                onClick={check}
                disabled={busy}
                className="flex items-center gap-2 rounded-xl border px-5 py-3 disabled:opacity-50"
              >
                <RefreshCw size={16} />
                {busy ? "Checking…" : "Refresh payment status"}
              </button>
              <Link
                to="/gift-cards"
                onClick={() => {
                  requestKey.current = null;
                  setPayment(null);
                }}
                className="block text-primary-800 underline"
              >
                Buy another gift
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-5">
              <label className="block">
                Recipient’s registered email
                <input
                  required
                  type="email"
                  name="recipientEmail"
                  maxLength={255}
                  value={form.recipientEmail}
                  onChange={change}
                  disabled={busy}
                  className="mt-2 w-full rounded-xl border p-3"
                />
              </label>
              <label className="block">
                Recipient’s registered phone
                <input
                  required
                  type="tel"
                  name="recipientPhone"
                  maxLength={20}
                  value={form.recipientPhone}
                  onChange={change}
                  disabled={busy}
                  className="mt-2 w-full rounded-xl border p-3"
                />
              </label>
              <label className="block">
                Gift amount (₹)
                <input
                  required
                  type="number"
                  min="1"
                  max="100000"
                  step="1"
                  name="amount"
                  value={form.amount}
                  onChange={change}
                  disabled={busy}
                  className="mt-2 w-full rounded-xl border p-3"
                />
              </label>
              <p className="text-sm text-slate-500">
                The recipient gets{" "}
                {Number(form.amount || 0).toLocaleString("en-IN")} points.
                Whole-rupee amounts only. Maximum ₹1,00,000 per gift.
              </p>
              <button
                disabled={busy}
                className="w-full rounded-xl bg-primary-800 px-6 py-4 font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Starting payment…" : "Pay securely & gift points"}
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
